import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Bug, Lightbulb, Monitor, Trash2 } from 'lucide-react';
import {
  fetchVorschlaege,
  loescheVorschlag,
  setzeVorschlagStatus,
  type Vorschlag,
} from '@/api/anliegen.api';
import { useConfirm } from '@/components/common/ConfirmDialog';

// ─── Was die Kunden zurückschicken ───────────────────────────────────────────
//
// Wünsche und Fehlermeldungen in einem Eingang. Getrennt wären es zwei Listen,
// von denen eine immer übersehen wird – und ob „das geht nicht" ein Fehler
// oder ein Wunsch ist, stellt sich ohnehin erst beim Lesen heraus.
//
// Der Status ist die einzige Pflege, die es braucht: gesehen, geplant,
// umgesetzt, abgelehnt. Wer eine Antwort dazuschreibt, sieht der Einreichende
// in seiner eigenen Liste – das ist der Unterschied zwischen einem Eingang
// und einem Briefkasten ohne Boden.

const STATUS = ['neu', 'gesehen', 'geplant', 'umgesetzt', 'abgelehnt'] as const;

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu',
  gesehen: 'Gesehen',
  geplant: 'Geplant',
  umgesetzt: 'Umgesetzt',
  abgelehnt: 'Abgelehnt',
};

const STATUS_FARBEN: Record<string, string> = {
  neu: 'bg-blue-50 text-blue-700',
  gesehen: 'bg-slate-100 text-slate-600',
  geplant: 'bg-violet-50 text-violet-700',
  umgesetzt: 'bg-green-50 text-green-700',
  abgelehnt: 'bg-slate-100 text-slate-500',
};

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function VorschlaegePage() {
  const [art, setArt] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'vorschlaege', art, status],
    queryFn: () => fetchVorschlaege({ art: art || undefined, status: status || undefined }),
  });

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <h1 className="text-base font-semibold text-slate-800 sm:text-lg">
          Vorschläge &amp; Fehlermeldungen
        </h1>
      </header>

      {/* Filter in einer Zeile über der Liste – nicht in einer Seitenspalte,
          die auf dem Telefon ohnehin verschwindet. */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={art}
            onChange={(e) => setArt(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            <option value="">Alle Arten</option>
            <option value="idee">Nur Ideen</option>
            <option value="fehler">Nur Fehler</option>
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            <option value="">Alle Status</option>
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
                {data?.zaehler[s] ? ` (${data.zaehler[s]})` : ''}
              </option>
            ))}
          </select>

          {data && data.zaehler.neu > 0 && status !== 'neu' && (
            <button
              onClick={() => setStatus('neu')}
              className="rounded-md bg-blue-50 px-2.5 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
            >
              {data.zaehler.neu} unbearbeitet
            </button>
          )}
        </div>
      </div>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-3">
          {isLoading && <p className="py-8 text-center text-sm text-slate-400">Wird geladen…</p>}

          {data?.data.length === 0 && !isLoading && (
            <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
              {art || status ? 'Nichts unter diesem Filter.' : 'Noch nichts eingegangen.'}
            </p>
          )}

          {data?.data.map((v) => (
            <VorschlagKarte key={v.id} vorschlag={v} />
          ))}
        </div>
      </main>
    </div>
  );
}

function VorschlagKarte({ vorschlag }: { vorschlag: Vorschlag }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [antwort, setAntwort] = useState(vorschlag.antwort ?? '');
  const [antwortOffen, setAntwortOffen] = useState(false);

  const aktualisieren = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'vorschlaege'] });

  const setzen = useMutation({
    mutationFn: (eingabe: { status: string; antwort?: string | null }) =>
      setzeVorschlagStatus(vorschlag.id, eingabe),
    onSuccess: async () => {
      await aktualisieren();
      setAntwortOffen(false);
      toast.success('Gespeichert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const loeschen = useMutation({
    mutationFn: () => loescheVorschlag(vorschlag.id),
    onSuccess: async () => {
      await aktualisieren();
      toast.success('Eintrag gelöscht.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const istFehler = vorschlag.art === 'fehler';
  const Symbol = istFehler ? Bug : Lightbulb;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 gap-3">
          <Symbol
            className={`mt-0.5 h-4 w-4 shrink-0 ${istFehler ? 'text-red-500' : 'text-amber-500'}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="font-medium text-slate-900">{vorschlag.titel}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{vorschlag.text}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_FARBEN[vorschlag.status] ?? STATUS_FARBEN.gesehen
          }`}
        >
          {STATUS_LABELS[vorschlag.status] ?? vorschlag.status}
        </span>
      </div>

      {/* Technische Angaben nur bei Fehlern – bei einer Idee gäbe es sie nicht
          zu sehen, weil sie gar nicht erst gespeichert werden. */}
      {(vorschlag.seite || vorschlag.browser) && (
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500">
          <Monitor className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
          {vorschlag.seite && <span className="font-mono">{vorschlag.seite}</span>}
          {vorschlag.browser && <span className="truncate">{vorschlag.browser}</span>}
        </p>
      )}

      {vorschlag.antwort && !antwortOffen && (
        <p className="mt-2 border-l-2 border-blue-200 pl-3 text-sm text-slate-600">
          <span className="block text-xs font-medium text-slate-400">Ihre Antwort</span>
          {vorschlag.antwort}
        </p>
      )}

      {antwortOffen && (
        <div className="mt-3">
          <textarea
            value={antwort}
            onChange={(e) => setAntwort(e.target.value)}
            rows={3}
            placeholder="Sichtbar für den Einreichenden."
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => {
                setAntwort(vorschlag.antwort ?? '');
                setAntwortOffen(false);
              }}
              className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              onClick={() => setzen.mutate({ status: vorschlag.status, antwort })}
              className="rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              Antwort speichern
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <p className="min-w-0 truncate text-xs text-slate-400">
          {vorschlag.email ?? 'unbekannt'} · {datum(vorschlag.createdAt)}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={vorschlag.status}
            onChange={(e) => setzen.mutate({ status: e.target.value })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs"
            aria-label="Status ändern"
          >
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>

          {!antwortOffen && (
            <button
              onClick={() => setAntwortOffen(true)}
              className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {vorschlag.antwort ? 'Antwort ändern' : 'Antworten'}
            </button>
          )}

          <button
            onClick={async () => {
              const ok = await confirm({
                title: 'Eintrag löschen',
                message: `„${vorschlag.titel}" wird endgültig gelöscht.`,
                confirmLabel: 'Löschen',
                variant: 'danger',
              });
              if (ok) loeschen.mutate();
            }}
            className="rounded-md border border-red-200 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50"
            aria-label="Eintrag löschen"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
